/**
 * useEntitlements / useFeatureActive
 *
 * Reads `public.member_active_features` (a view that already filters by
 * status, expiry, and counter exhaustion). The view has security_invoker
 * so RLS on `alacarte_purchases` (member_id = auth.uid()) applies — a
 * member only ever sees their own rows.
 *
 * Founder override: if the caller is a founder (per useFounder), every
 * à la carte feature is reported as eternally active with a synthetic
 * row. The server-side founders trigger pins the tier to tier_attuned
 * separately; this hook just makes the UI reflect the full grant.
 *
 * Frontend rule: NEVER trust this hook for security gates. It's UI only.
 * Every server-side action must re-check via the same view (or a server
 * RPC) using the caller's JWT.
 */

import { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useFounder } from "@/lib/founder/useFounder";
import {
  ALACARTE_ITEMS,
  type AlacarteFeatureKey,
  type PurchaseType,
} from "@/lib/billing/alacarte";

export interface ActiveFeature {
  feature_key: AlacarteFeatureKey;
  purchase_type: PurchaseType;
  expires_at: string | null;
  metadata: Record<string, unknown>;
  is_active: boolean;
}

/** Synthetic "founder grant" rows — one per catalog feature, never expires. */
const buildFounderFeatures = (): ActiveFeature[] =>
  ALACARTE_ITEMS.map((item) => ({
    feature_key: item.key,
    // Prefer monthly when available; otherwise one_time. Purely cosmetic.
    purchase_type:
      (item.options.find((o) => o.type === "monthly")?.type ??
        item.options[0]?.type ??
        "one_time") as PurchaseType,
    expires_at: null,
    metadata: { source: "founder_grant" },
    is_active: true,
  }));

export const useEntitlements = () => {
  const { user } = useAuth();
  const { isFounder } = useFounder();
  const [features, setFeatures] = useState<ActiveFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setFeatures([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from("member_active_features")
      .select("feature_key, purchase_type, expires_at, metadata, is_active")
      .eq("member_id", user.id);
    if (error) setError(error.message);
    setFeatures((data ?? []) as ActiveFeature[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  // Realtime: refresh when this member's purchases change. Webhook writes
  // are what trigger this — gives us the <100ms activation promised in
  // the spec without polling.
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`alacarte-${user.id}-${Math.random().toString(36).slice(2, 10)}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "alacarte_purchases",
          filter: `member_id=eq.${user.id}`,
        },
        () => {
          void load();
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user, load]);

  // Founder override — merge synthetic grants over real rows.
  const effectiveFeatures = useMemo<ActiveFeature[]>(() => {
    if (!isFounder) return features;
    const realByKey = new Map(features.map((f) => [f.feature_key, f]));
    for (const grant of buildFounderFeatures()) {
      // Real active rows win (preserves real expiry / metadata for analytics).
      if (!realByKey.get(grant.feature_key)?.is_active) {
        realByKey.set(grant.feature_key, grant);
      }
    }
    return Array.from(realByKey.values());
  }, [features, isFounder]);

  return { features: effectiveFeatures, loading, error, reload: load };
};

/**
 * UI-only check. Returns true if the member has any active row for this
 * feature. Use for "owned" badges, conditional rendering, etc.
 */
export const useFeatureActive = (key: AlacarteFeatureKey) => {
  const { features, loading } = useEntitlements();
  const active = features.some((f) => f.feature_key === key && f.is_active);
  return { active, loading };
};
